import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useConversationDetailsContext } from "@/context/ConversationDetailsContext";
import {
  PARTICIPANTS_GET,
  getConversationParticipants,
} from "@/api/client/participant";
import { Client, ErrorSubcode, Participant, User } from "@/types";
import { parseApiError } from "@/lib/errorUtils";

interface UseSpeakerAssignmentReturn {
  assignToSpeaker: (
    speakerId: string,
    participantId: string,
  ) => Promise<void>;
  unassignFromSpeaker: (speakerId: string) => Promise<void>;
  createAndAssign: (
    speakerId: string,
    data: Client | User,
    type: "client" | "user",
  ) => Promise<void>;
}

function isSpeakerConflictError(error: unknown): boolean {
  const { subcode, statusCode } = parseApiError(error);
  return statusCode === 409 || subcode === ErrorSubcode.ALREADY_EXISTS;
}

export const useSpeakerAssignment = (): UseSpeakerAssignmentReturn => {
  const {
    conversationId,
    participants,
    markSpeakerUpdated,
    createParticipantAsync,
    updateParticipantAsync,
    transcriptMessages,
  } = useConversationDetailsContext();
  const queryClient = useQueryClient();

  // After an assignment, if exactly 1 speaker and 1 participant remain
  // unassigned, auto-link them via process of elimination. Best-effort —
  // failures must not propagate to the manual action that triggered this.
  //
  // `excludeSpeakerIds` should contain the source speakers of any participants
  // moved by this action. Excluding them prevents the auto-link from "swapping"
  // a displaced participant onto a speaker that was just vacated by the user's
  // explicit move. Speakers that were already empty (not just vacated) remain
  // eligible — so the obvious last-mapping still gets filled in.
  const tryAutoLinkRemaining = useCallback(
    async (excludeSpeakerIds: string[] = []) => {
      try {
        if (!transcriptMessages?.length) return;
        const allParticipants =
          queryClient.getQueryData<Participant[]>([
            PARTICIPANTS_GET,
            conversationId,
          ]) ?? participants;

        const transcriptSpeakerIds = new Set(
          transcriptMessages.map((s) => s.speakerId).filter(Boolean),
        );
        const assignedSpeakerIds = new Set(
          allParticipants
            .filter((p) => p.speakerId)
            .map((p) => p.speakerId),
        );
        const rawUnassignedSpeakerIds = [...transcriptSpeakerIds].filter(
          (id) => !assignedSpeakerIds.has(id),
        );
        const unassignedParticipants = allParticipants.filter(
          (p) => !p.speakerId && (p.clientId || p.userId),
        );

        // Only fire when there is genuinely ONE unassigned speaker and ONE
        // unassigned participant. If multiple speakers are unassigned, the
        // exclusion would otherwise let us "pick" the non-excluded one —
        // that's guessing, not process-of-elimination.
        const excludedSpeakers = new Set(excludeSpeakerIds);
        if (
          rawUnassignedSpeakerIds.length === 1 &&
          unassignedParticipants.length === 1 &&
          !excludedSpeakers.has(rawUnassignedSpeakerIds[0])
        ) {
          await updateParticipantAsync({
            id: unassignedParticipants[0].id,
            conversationId,
            speakerId: rawUnassignedSpeakerIds[0],
          });
        }
      } catch {
        // Best-effort; never propagate
      }
    },
    [conversationId, participants, transcriptMessages, queryClient, updateParticipantAsync],
  );

  const assignToSpeaker = useCallback(
    async (speakerId: string, participantId: string) => {
      const freshParticipants =
        queryClient.getQueryData<Participant[]>([
          PARTICIPANTS_GET,
          conversationId,
        ]) ?? participants;

      const currentHolder = freshParticipants.find(
        (p: Participant) =>
          p.speakerId === speakerId && p.id !== participantId,
      );

      // Capture the participant's old speaker BEFORE we reassign — this is
      // the "freshly vacated" speaker that should be excluded from auto-link.
      const sourceSpeakerId = freshParticipants.find(
        (p: Participant) => p.id === participantId,
      )?.speakerId;

      if (currentHolder) {
        const originalSpeakerId = currentHolder.speakerId;

        await updateParticipantAsync({
          id: currentHolder.id,
          conversationId,
          speakerId: null,
        });

        try {
          await updateParticipantAsync({
            id: participantId,
            conversationId,
            speakerId,
          });
        } catch (error) {
          try {
            await updateParticipantAsync({
              id: currentHolder.id,
              conversationId,
              speakerId: originalSpeakerId,
            });
          } catch (rollbackError) {
            console.error("Failed to roll back participant reassignment", {
              rollbackError,
              currentHolderId: currentHolder.id,
              participantId,
              speakerId,
              conversationId,
            });
          }
          throw error;
        }
      }

      if (!currentHolder) {
        // No holder found: try direct assignment
        try {
          await updateParticipantAsync({
            id: participantId,
            conversationId,
            speakerId,
          });
        } catch (error) {
          // On speaker-conflict error, fetch fresh data and retry via raw API
          // to avoid spurious toast from the mutation's onError
          if (isSpeakerConflictError(error)) {
            const freshParticipants =
              await getConversationParticipants(conversationId);
            const realHolder = freshParticipants.find(
              (p: Participant) =>
                p.speakerId === speakerId && p.id !== participantId,
            );
            if (realHolder) {
              await updateParticipantAsync({
                id: realHolder.id,
                conversationId,
                speakerId: null,
              });
              await updateParticipantAsync({
                id: participantId,
                conversationId,
                speakerId,
              });
            } else {
              throw error;
            }
          } else {
            throw error;
          }
        }
      }

      markSpeakerUpdated();
      // The freshly-vacated speaker is the one this participant just left.
      // Don't auto-link anyone onto it — that would be a surprising swap.
      await tryAutoLinkRemaining(sourceSpeakerId ? [sourceSpeakerId] : []);
    },
    [conversationId, participants, queryClient, updateParticipantAsync, markSpeakerUpdated, tryAutoLinkRemaining],
  );

  const unassignFromSpeaker = useCallback(
    async (speakerId: string) => {
      const freshParticipants =
        queryClient.getQueryData<Participant[]>([
          PARTICIPANTS_GET,
          conversationId,
        ]) ?? participants;

      const participant = freshParticipants.find(
        (p: Participant) => p.speakerId === speakerId,
      );
      if (!participant) return;

      await updateParticipantAsync({
        id: participant.id,
        conversationId,
        speakerId: null,
      });

      markSpeakerUpdated();
    },
    [conversationId, participants, queryClient, updateParticipantAsync, markSpeakerUpdated],
  );

  const createAndAssign = useCallback(
    async (
      speakerId: string,
      data: Client | User,
      type: "client" | "user",
    ) => {
      const freshParticipants =
        queryClient.getQueryData<Participant[]>([
          PARTICIPANTS_GET,
          conversationId,
        ]) ?? participants;
      const existingHolder = freshParticipants.find(
        (p: Participant) => p.speakerId === speakerId,
      );

      if (existingHolder) {
        await updateParticipantAsync({
          id: existingHolder.id,
          conversationId,
          speakerId: null,
        });
      }

      try {
        const participant = await createParticipantAsync({
          conversationId,
          name: `${data.firstname} ${data.lastname}`,
          email: data.email || undefined,
          speakerId,
          ...(type === "client" ? { clientId: data.id } : { userId: data.id }),
        });

        if (!participant?.id) {
          throw new Error("Participant creation did not return an ID");
        }
      } catch (error) {
        if (existingHolder) {
          try {
            await updateParticipantAsync({
              id: existingHolder.id,
              conversationId,
              speakerId,
            });
          } catch (rollbackError) {
            console.error("Failed to roll back participant unassignment", {
              rollbackError,
              existingHolderId: existingHolder.id,
              speakerId,
              conversationId,
            });
          }
        }
        throw error;
      }

      markSpeakerUpdated();
      // Brand-new participants have no source speaker — nothing to exclude.
      await tryAutoLinkRemaining([]);
    },
    [conversationId, participants, queryClient, createParticipantAsync, updateParticipantAsync, markSpeakerUpdated, tryAutoLinkRemaining],
  );

  return {
    assignToSpeaker,
    unassignFromSpeaker,
    createAndAssign,
  };
};
